import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { InvoiceStatusEnum } from "domain/enum/invoiceStatus";
import { MessageTypeEnum } from "domain/enum/messageType";
import Spinner from "presentation/components/Shared/Spinner";
import { useInvoicesById, useUpdateInvoiceMutation } from "presentation/hooks/use-query-invoice";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";

interface ParamProps {
  document: Document | null;
  onClose(): void;
  showMessage(
    type: MessageTypeEnum,
    subject: string,
    message: string
  ): void;
}

const ApprovalForm: React.FC<ParamProps> = (params: ParamProps) => {

  const toast = useRef<Toast>(null);
  const { userEmployee, email: userEmployeeEmail } = useAuthStore();
  const [url, setUrl] = useState("");
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
  const [reasonReject, setReasonReject] = useState("");
  const possibleApprovalStatus = ["Enviado"];

  const { mutate: updateInvoice, isSuccess: isSuccessUpdateInvoice, isError: isErrorUpdateInvoice, isPending: isPendingUpdateInvoice } =
    useUpdateInvoiceMutation();

  const { data: invoiceData } = useInvoicesById(
    params.document?.invoice?.id
  );


  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!params.document?.fullPath) return;

      try {
        const response = await httpClient.get(
          `/assets/${params.document.fullPath}`
        );
        setUrl(response.data.url);
      } catch (err) {
        console.error("Erro ao buscar signed URL:", err);
      }
    };

    fetchSignedUrl();
  }, [params.document]);



  useEffect(() => {
    if (isSuccessUpdateInvoice) {
      if (invoiceData?.data?.status === InvoiceStatusEnum.APROVADO) {
        params.showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          "Documento foi aprovado com sucesso"
        );
      } else if (invoiceData?.data?.status === InvoiceStatusEnum.NAO_APROVADO) {
        params.showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          "Documento foi reprovado com sucesso"
        );
      }

      params.onClose();
    }
  }, [isSuccessUpdateInvoice]);


  useEffect(() => {
    if (isErrorUpdateInvoice) {
      if (invoiceData?.data?.status === InvoiceStatusEnum.APROVADO) {
        params.showMessage(
          MessageTypeEnum.ERROR,
          "Workflow",
          "Erro ao aprovar documento"
        );
      } else if (invoiceData?.data?.status === InvoiceStatusEnum.NAO_APROVADO) {
        params.showMessage(
          MessageTypeEnum.ERROR,
          "Workflow",
          "Erro ao reprovar documento"
        );
      }

      params.onClose();
    }
  }, [isErrorUpdateInvoice]);

  const handleApprove = () => {
    const invoice = invoiceData?.data;
    if (invoice && invoice.status === InvoiceStatusEnum.ENVIADO && userEmployeeEmail) {
      invoice.status = InvoiceStatusEnum.APROVADO;
      invoice.lastModifiedBy = userEmployeeEmail;
      updateInvoice(invoice);
    }
    setShowConfirm(false);
    setConfirmAction(null);
  };

  const handleReject = () => {
    const invoice = invoiceData?.data;
    if (invoice && invoice.status === InvoiceStatusEnum.ENVIADO && userEmployeeEmail) {
      invoice.status = InvoiceStatusEnum.NAO_APROVADO;
      invoice.rejectionReason = reasonReject;
      invoice.lastModifiedBy = userEmployeeEmail;
      updateInvoice(invoice);
    }
    setVisibleRejectionReason(false);
    setConfirmAction(null);
  };

  if (isPendingUpdateInvoice) return <Spinner />;

  return (
    <div>
      {" "}
      <div>
        <ConfirmDialog
          visible={showConfirm}
          onHide={() => setShowConfirm(false)}
          message={
            confirmAction === 'approve'
              ? 'Deseja aprovar este documento?'
              : 'Deseja rejeitar este documento, sendo este o motivo?'}
          header="Confirmação"
          icon="pi pi-exclamation-triangle"
          accept={() => {
            if (confirmAction === "approve") handleApprove();
            if (confirmAction === "reject") {
              setVisibleRejectionReason(false);
              handleReject();
            }
          }}
          reject={() => setShowConfirm(false)}
          acceptLabel="Sim"
          rejectLabel="Não"
        />
        {url.includes('.pdf') ? (
          <iframe
            src={`${url}#navpanes=0`}
            width="100%"
            height="700px"
            title="Invoice"
            style={{ border: "none" }}
          ></iframe>) :
          (
            <img src={url}
              width="100%"
              height="auto"
              title="Invoice"
              style={{ border: "none" }}>
            </img>
          )
        }

        {possibleApprovalStatus.includes(params.document?.invoice?.status ?? "") && (
          <div style={{ textAlign: "right", marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "15px" }}>
            <Button
              label="Rejeitar"
              className="p-button-danger"
              icon="pi pi-times"
              onClick={() => {
                setReasonReject("");
                setVisibleRejectionReason(true);
              }}
            />
            <Button
              label="Aprovar"
              icon="pi pi-check"
              onClick={() => {
                setConfirmAction("approve");
                setShowConfirm(true);
              }}
            />
          </div>
        )
        }

      </div>
      <Dialog
        header="Rejeição do Documento"
        visible={visibleRejectionReason}
        style={{ width: "30vw" }}
        modal
        onHide={() => setVisibleRejectionReason(false)}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="motivo">Informe o Motivo da Rejeição:</label>
            <InputTextarea
              id="motivo"
              value={reasonReject}
              onChange={(e) => setReasonReject(e.target.value)}
              rows={4}
              autoFocus
              maxLength={190}
            />
            <div className="flex justify-content-end">
              <small>{reasonReject?.length}/190 caracteres</small>
            </div>
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setVisibleRejectionReason(false)}
            />
            <Button
              label="Rejeitar"
              icon="pi pi-check"
              onClick={() => {
                if (reasonReject.trim() !== "") {
                  setConfirmAction("reject");
                  setShowConfirm(true);
                } else {
                  toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Informe o motivo da não aprovação do documento.",
                    life: 6000,
                  });
                }
              }}
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      <Toast ref={toast} position="bottom-right" />

    </div>
  );
};

export default ApprovalForm;
